import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Eye,
  HelpCircle,
  MessageSquare,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { AuditorCommentType, AuditorReviewStatus } from '@deepguide-ai/types'

type IconConfig = { Icon: LucideIcon; colorClass: string }

// Icons + colors mirror the auditor-center review-action buttons
// (ReviewActionDialog) so a status reads the same everywhere. A set review
// status outranks the comment type — it's the stronger signal — matched first.
const REVIEW_STATUS_ICON: Record<AuditorReviewStatus, IconConfig> = {
  NEEDS_REMEDIATION: { Icon: AlertTriangle, colorClass: 'text-status-orange' },
  EXCEPTION_NOTED: { Icon: Ban, colorClass: 'text-status-red' },
  REVIEWED: { Icon: Eye, colorClass: 'text-status-blue-text' },
  SATISFACTORY: { Icon: CheckCircle2, colorClass: 'text-status-green' },
}

// Bare comment types have no review-action button — fall back to the icon of
// the review status each implies (EVIDENCE_REQUEST ⇒ remediation, EXCEPTION ⇒
// exception); CLARIFICATION has no equivalent.
const COMMENT_TYPE_ICON: Record<AuditorCommentType, IconConfig> = {
  EVIDENCE_REQUEST: { Icon: AlertTriangle, colorClass: 'text-status-orange' },
  CLARIFICATION: { Icon: HelpCircle, colorClass: 'text-status-orange' },
  EXCEPTION: { Icon: Ban, colorClass: 'text-status-red' },
  NOTE: { Icon: MessageSquare, colorClass: 'text-text-subtle' },
}

/**
 * Icon for an auditor review status, falling back to the comment type when no
 * status is set — mirrors how the verb label is derived. Reusable across the
 * Open Requests list, auditor threads, and the audit cycle views.
 */
export function ReviewStatusIcon({
  reviewStatus,
  commentType,
  className,
}: {
  reviewStatus?: AuditorReviewStatus | null
  commentType?: AuditorCommentType | null
  className?: string
}) {
  const { Icon, colorClass } =
    (reviewStatus && REVIEW_STATUS_ICON[reviewStatus]) ||
    (commentType && COMMENT_TYPE_ICON[commentType]) ||
    COMMENT_TYPE_ICON.NOTE

  return <Icon className={cn('h-4 w-4 shrink-0', colorClass, className)} />
}
